/**
 * React hook for the Writing Assistant feature.
 * Provides state management and API integration for text enhancement.
 */
import { useState, useCallback, useRef } from "react";
import {
  enhanceText,
  enhanceTextStream,
  EnhancementRequest,
  EnhancementResponse,
  EnhancementStreamChunk,
  LLMModelChoice,
  DEFAULT_ENHANCEMENT_PROMPT,
} from "../lib/api/writingAssistant";

/**
 * State of the writing assistant.
 */
export interface WritingAssistantState {
  /** Question/query that the document should answer */
  question: string;
  /** Original text entered by user */
  originalText: string;
  /** Enhanced text from LLM */
  enhancedText: string;
  /** Whether enhancement is in progress */
  isEnhancing: boolean;
  /** Error message if enhancement failed */
  error: string | null;
  /** ID of the prompt used for enhancement */
  promptId: string | null;
  /** Processing time in milliseconds */
  processingTimeMs: number | null;
  /** Token count (if available) */
  tokenCount: number | null;
}

/**
 * Return type of useWritingAssistant hook.
 */
export interface UseWritingAssistantReturn extends WritingAssistantState {
  /** Set the question */
  setQuestion: (question: string) => void;
  /** Set the original text */
  setOriginalText: (text: string) => void;
  /** Set the enhanced text (for manual edits) */
  setEnhancedText: (text: string) => void;
  /** Enhance the original text using LLM */
  enhance: (promptId?: string, model?: LLMModelChoice, customPrompt?: string) => Promise<void>;
  /** Enhance with streaming response */
  enhanceStreaming: (promptId?: string, model?: LLMModelChoice, customPrompt?: string) => Promise<void>;
  /** Clear all state */
  clear: () => void;
  /** Clear only enhanced text and error */
  clearEnhanced: () => void;
  /** Whether there's content to enhance */
  canEnhance: boolean;
  /** Abort current streaming enhancement */
  abort: () => void;
}

export type { LLMModelChoice };

/**
 * Options for useWritingAssistant hook.
 */
export interface UseWritingAssistantOptions {
  /** Initial question */
  initialQuestion?: string;
  /** Initial original text */
  initialText?: string;
  /** Callback when enhancement completes */
  onEnhanceComplete?: (response: EnhancementResponse) => void;
  /** Callback when enhancement fails */
  onEnhanceError?: (error: Error) => void;
  /** Callback on each stream chunk */
  onStreamChunk?: (chunk: EnhancementStreamChunk) => void;
}

/**
 * Hook for managing text enhancement with LLM.
 *
 * @example
 * ```tsx
 * const {
 *   originalText,
 *   setOriginalText,
 *   enhancedText,
 *   isEnhancing,
 *   error,
 *   enhance,
 *   enhanceStreaming,
 *   canEnhance,
 * } = useWritingAssistant();
 *
 * // Enhance with streaming
 * await enhanceStreaming();
 * ```
 */
export function useWritingAssistant(
  options: UseWritingAssistantOptions = {}
): UseWritingAssistantReturn {
  const {
    initialQuestion = "",
    initialText = "",
    onEnhanceComplete,
    onEnhanceError,
    onStreamChunk,
  } = options;

  // State
  const [question, setQuestion] = useState(initialQuestion);
  const [originalText, setOriginalText] = useState(initialText);
  const [enhancedText, setEnhancedText] = useState("");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptId, setPromptId] = useState<string | null>(null);
  const [processingTimeMs, setProcessingTimeMs] = useState<number | null>(null);
  const [tokenCount, setTokenCount] = useState<number | null>(null);

  // Abort controller for streaming
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Check if there's content to enhance.
   */
  const canEnhance = question.trim().length > 0 && originalText.trim().length > 0 && !isEnhancing;

  /**
   * Clear all state.
   */
  const clear = useCallback(() => {
    setQuestion("");
    setOriginalText("");
    setEnhancedText("");
    setError(null);
    setPromptId(null);
    setProcessingTimeMs(null);
    setTokenCount(null);
  }, []);

  /**
   * Clear only enhanced text and error.
   */
  const clearEnhanced = useCallback(() => {
    setEnhancedText("");
    setError(null);
    setPromptId(null);
    setProcessingTimeMs(null);
    setTokenCount(null);
  }, []);

  /**
   * Abort current streaming enhancement.
   */
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsEnhancing(false);
    }
  }, []);

  /**
   * Enhance text using LLM (non-streaming).
   * @param customPromptId - Optional prompt ID to use
   * @param model - Optional LLM model choice
   * @param customPrompt - Optional custom prompt text (takes precedence over promptId)
   */
  const enhance = useCallback(
    async (customPromptId?: string, model?: LLMModelChoice, customPrompt?: string) => {
      if (!question.trim()) {
        setError("Please enter a question");
        return;
      }
      if (!originalText.trim()) {
        setError("Please enter text to enhance");
        return;
      }

      setIsEnhancing(true);
      setError(null);
      setEnhancedText("");
      setProcessingTimeMs(null);
      setTokenCount(null);

      try {
        // Build request: custom prompt takes precedence over prompt_id
        // If neither provided, use DEFAULT_ENHANCEMENT_PROMPT
        const request: EnhancementRequest = {
          question: question,
          original_text: originalText,
          model: model,
          ...(customPrompt
            ? { prompt_text: customPrompt }
            : customPromptId
              ? { prompt_id: customPromptId }
              : { prompt_text: DEFAULT_ENHANCEMENT_PROMPT }
          ),
        };

        const response = await enhanceText(request);

        setEnhancedText(response.enhanced_text);
        setPromptId(response.prompt_id);
        setProcessingTimeMs(response.processing_time_ms);
        setTokenCount(response.token_count ?? null);

        onEnhanceComplete?.(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Enhancement failed";
        setError(errorMessage);
        onEnhanceError?.(err instanceof Error ? err : new Error(errorMessage));
      } finally {
        setIsEnhancing(false);
      }
    },
    [question, originalText, onEnhanceComplete, onEnhanceError]
  );

  /**
   * Enhance text using LLM with streaming response.
   * @param customPromptId - Optional prompt ID to use
   * @param model - Optional LLM model choice
   * @param customPrompt - Optional custom prompt text (takes precedence over promptId)
   */
  const enhanceStreaming = useCallback(
    async (customPromptId?: string, model?: LLMModelChoice, customPrompt?: string) => {
      if (!question.trim()) {
        setError("Please enter a question");
        return;
      }
      if (!originalText.trim()) {
        setError("Please enter text to enhance");
        return;
      }

      // Abort any existing stream
      abort();

      setIsEnhancing(true);
      setError(null);
      setEnhancedText("");
      setProcessingTimeMs(null);
      setTokenCount(null);

      abortControllerRef.current = new AbortController();

      try {
        // Build request: custom prompt takes precedence over prompt_id
        // If neither provided, use DEFAULT_ENHANCEMENT_PROMPT
        const request: EnhancementRequest = {
          question: question,
          original_text: originalText,
          model: model,
          ...(customPrompt
            ? { prompt_text: customPrompt }
            : customPromptId
              ? { prompt_id: customPromptId }
              : { prompt_text: DEFAULT_ENHANCEMENT_PROMPT }
          ),
        };

        let accumulatedText = "";

        for await (const chunk of enhanceTextStream(request)) {
          // Check for abort
          if (abortControllerRef.current?.signal.aborted) {
            break;
          }

          // Handle error in chunk
          if (chunk.error) {
            setError(chunk.error);
            onEnhanceError?.(new Error(chunk.error));
            break;
          }

          // Accumulate content
          if (chunk.content) {
            accumulatedText += chunk.content;
            setEnhancedText(accumulatedText);
          }

          // Call stream callback
          onStreamChunk?.(chunk);

          // Handle final chunk
          if (chunk.done) {
            if (chunk.prompt_id) {
              setPromptId(chunk.prompt_id);
            }
            if (chunk.processing_time_ms) {
              setProcessingTimeMs(chunk.processing_time_ms);
            }

            // Create response object for callback
            if (!chunk.error) {
              const response: EnhancementResponse = {
                enhanced_text: accumulatedText,
                prompt_id: chunk.prompt_id || "",
                processing_time_ms: chunk.processing_time_ms || 0,
              };
              onEnhanceComplete?.(response);
            }
          }
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : "Enhancement failed";
        setError(errorMessage);
        onEnhanceError?.(err instanceof Error ? err : new Error(errorMessage));
      } finally {
        setIsEnhancing(false);
        abortControllerRef.current = null;
      }
    },
    [question, originalText, abort, onEnhanceComplete, onEnhanceError, onStreamChunk]
  );

  return {
    // State
    question,
    originalText,
    enhancedText,
    isEnhancing,
    error,
    promptId,
    processingTimeMs,
    tokenCount,
    // Actions
    setQuestion,
    setOriginalText,
    setEnhancedText,
    enhance,
    enhanceStreaming,
    clear,
    clearEnhanced,
    abort,
    // Computed
    canEnhance,
  };
}

export default useWritingAssistant;
